import { useQuery } from "@tanstack/react-query";
import { useState, useEffect, useMemo } from "react";
import { useDeepCompareMemo } from "@/hooks/use-deep-compare-memo";
import { Music, Calendar, Building, ChevronDown, HelpCircle, BarChart3, Smartphone, RefreshCw, Plus, CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { HelpModal } from "@/components/help-modal";
import { PWAInstallModal } from "@/components/pwa-install-modal";
import { useLocation } from "wouter";
import CreateEventModal from "@/components/create-event-modal";
import { AppHeader } from "@/components/app-header";
import { PageBackground } from "@/components/ui/page-background";
import { useAuth } from "@/hooks/use-auth";
import { usePWADetection } from "@/hooks/use-pwa-detection";
import { usePWANavigationGuard } from "@/hooks/use-pwa-navigation-guard";
import { EventCard } from "@/components/event-card";
import { Squircle } from "@/components/ui/squircle";
import type { EventWithOrganisationAndPerformerCount, Organisation } from "@shared/schema";

async function fetchEvents(): Promise<EventWithOrganisationAndPerformerCount[]> {
  const response = await fetch('/api/events');
  if (!response.ok) {
    if (response.status === 401) {
      window.dispatchEvent(new Event('auth-error'));
    }
    throw new Error('Failed to fetch events');
  }
  return response.json();
}

async function fetchOrganisations(): Promise<Organisation[]> {
  const response = await fetch('/api/organisations');
  if (!response.ok) {
    if (response.status === 401) {
      window.dispatchEvent(new Event('auth-error'));
    }
    throw new Error('Failed to fetch organisations');
  }
  return response.json();
}

type GroupedEvents = Record<string, { org: Organisation, upcomingEvents: EventWithOrganisationAndPerformerCount[], pastEvents: EventWithOrganisationAndPerformerCount[] }>;

export default function EventManagement() {
  const [, setLocation] = useLocation();
  const [showCreateModal, setShowCreateModal] = useState<false | number>(false);
  const [preventNavigation] = useState<Record<number, boolean>>({});
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [showPWAInstallModal, setShowPWAInstallModal] = useState(false);

  // Prevent back navigation in PWA to avoid going back to login page
  usePWANavigationGuard({ preventBack: true });
  const [expandedPastEvents, setExpandedPastEvents] = useState<Record<string, boolean>>({});
  const [searchTerms, setSearchTerms] = useState<Record<string, string>>({});
  const [isRefreshing, setIsRefreshing] = useState(false);
  const { user, isLoading: isAuthLoading } = useAuth();
  const { isPWA, shouldShowInstallButton } = usePWADetection();
  
  // Mobile detection for PWA install button
  const isMobile = useMemo(() => {
    if (typeof window === 'undefined') return false;
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  }, []);

  // Service worker registration (without automatic reload)
  useEffect(() => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.ready.then(registration => {
        // Register the service worker but don't auto-update
        // User can manually update with the Force Update button
      });
    }
  }, []);

  const { data: events = [], isLoading: isLoadingEvents } = useQuery<EventWithOrganisationAndPerformerCount[]>({
    queryKey: ["events"],
    queryFn: fetchEvents,
  });

  const { data: organisations = [], isLoading: isLoadingOrganisations } = useQuery<Organisation[]>({
    queryKey: ["organisations"],
    queryFn: fetchOrganisations,
    enabled: !isAuthLoading && user?.role === 'god',
  });


  const handleSelectEvent = (eventId: number) => {
    // Prevent navigation if a dropdown action is in progress
    if (preventNavigation[eventId]) {
      return;
    }
    setLocation(`/event/${eventId}`);
  };


  const groupedEvents = useDeepCompareMemo(() => {
    const groups: GroupedEvents = {};
    let orgs: (Organisation | undefined)[] = [];

    if (user?.role === 'god') {
      orgs = organisations;
    } else {
      orgs = user?.organisationUsers?.map(ou => ou.organisation) ?? [];
    }

    orgs.forEach((organisation) => {
      if (organisation) {
        const orgName = organisation.name || 'Uncategorised Events';
        if (!groups[orgName]) {
          groups[orgName] = { org: organisation, upcomingEvents: [], pastEvents: [] };
        }
      }
    });

    const currentDate = new Date();
    currentDate.setHours(0, 0, 0, 0); // Start of today

    events?.forEach((events) => {
      if (events.organisation) {
        const orgName = events.organisation.name || 'Uncategorised Events';
        if (groups[orgName]) {
          const isPast = new Date(events.date) < currentDate;
          if (isPast) {
            groups[orgName].pastEvents.push(events);
          } else {
            groups[orgName].upcomingEvents.push(events);
          }
        }
      }
    });

    // Sort events within each organization
    Object.keys(groups).forEach(orgName => {
      groups[orgName].pastEvents.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
      groups[orgName].upcomingEvents.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
    });

    // Filter events based on search terms
    Object.keys(groups).forEach(orgName => {
      const searchTerm = searchTerms[orgName]?.toLowerCase() || '';
      if (searchTerm) {
        groups[orgName].upcomingEvents = groups[orgName].upcomingEvents.filter(event =>
          event.name.toLowerCase().includes(searchTerm)
        );
        groups[orgName].pastEvents = groups[orgName].pastEvents.filter(event =>
          event.name.toLowerCase().includes(searchTerm)
        );
      }
    });

    return groups;
  }, [events, user, organisations, searchTerms]);

  const userIsOwnerOfOrg = (organisationId: number | undefined) => {
    if (!organisationId) return false;
    if (user?.role === 'god') return true;
    
    const isOwner = user?.organisationUsers?.some((ou) => {
      return ou.organisation.id === organisationId && ou.role === 'owner';
    });
    
    return isOwner || false;
  }

  const handleCreateEvent = (orgId: number) => {
    if (!orgId) {
      return;
    }
    setShowCreateModal(orgId);
  };

  const togglePastEvents = (orgName: string) => {
    setExpandedPastEvents(prev => ({
      ...prev,
      [orgName]: !prev[orgName],
    }));
  };

  const handleSearchChange = (orgName: string, value: string) => {
    setSearchTerms(prev => ({
      ...prev,
      [orgName]: value,
    }));
  };

  const handleForceRefresh = () => {
    // Show animation immediately
    setIsRefreshing(true);
    
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.ready.then(registration => {
        // Force check for updates
        registration.update().then(() => {
          // If no update found, still refresh to clear cache
          window.location.reload();
        });
      });
    } else {
      // Fallback for browsers without service worker support
      window.location.reload();
    }
  };

  

  const isLoading = isAuthLoading || isLoadingEvents || (user?.role === 'god' && isLoadingOrganisations);

  // Show refreshing animation during browser reload
  if (isRefreshing) {
    return (
      <PageBackground>
        {user?.role === 'god' ? (
          <AppHeader />
        ) : (
          <AppHeader username={user?.username} />
        )}
        <div className="flex items-center justify-center relative" style={{ minHeight: 'calc(100vh - 64px)' }}>
          <div className="text-center relative z-10">
            <div className="relative mb-4">
              <div className="w-12 h-12 mx-auto border-3 border-blue-200 rounded-full animate-spin border-t-blue-600"></div>
            </div>
            <h2 className="text-lg font-semibold text-slate-800 mb-1 drop-shadow-sm">
              Refreshing...
            </h2>
            <p className="text-slate-600 drop-shadow-sm text-sm">
              Getting the latest updates
            </p>
          </div>
        </div>
      </PageBackground>
    );
  }

  if (isLoading) {
    return (
      <PageBackground>
        {user?.role === 'god' ? (
          <AppHeader />
        ) : (
          <AppHeader username={user?.username} />
        )}
        <div className="flex items-center justify-center relative" style={{ minHeight: 'calc(100vh - 64px)' }}>
          <div className="text-center relative z-10">
            <Music className="h-8 w-8 animate-pulse mx-auto mb-4 text-slate-700 drop-shadow-sm" />
            <p className="text-slate-700 drop-shadow-sm">Loading your events...</p>
          </div>
        </div>
      </PageBackground>
    );
  }

  return (
    <PageBackground>
      {user?.role === 'god' ? (
        <AppHeader />
      ) : (
        <AppHeader username={user?.username} />
      )}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2 relative z-10">
          <div className="my-2 mb-4">
            <div className="flex flex-col md:flex-row gap-2">
              {/* Help and Manage buttons - side by side on mobile */}
              <div className="flex flex-row gap-2 w-full md:w-auto">
                <Button
                  variant="outline"
                  onClick={() => setShowHelpModal(true)}
                  className="flex-1 md:w-auto flex items-center gap-2"
                >
                  <HelpCircle className="h-4 w-4" />
                  Help?
                </Button>
              </div>
              {isPWA ? (
                <Button
                  variant="outline"
                  onClick={handleForceRefresh}
                  disabled={isRefreshing}
                  className="w-full md:w-auto flex items-center gap-2 bg-green-50/40 backdrop-blur-md hover:bg-green-100/50 border-green-200/60 text-green-700 shadow-lg transition-all duration-300 disabled:opacity-50"
                  style={{
                    boxShadow: '0 4px 16px rgba(34,197,94,0.1), inset 0 1px 0 rgba(255,255,255,0.3)',
                  }}
                >
                  <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
                  {isRefreshing ? 'Refreshing...' : 'Force Update'}
                </Button>
              ) : isMobile ? (
                <Button
                  variant="outline"
                  onClick={() => setShowPWAInstallModal(true)}
                  className="w-full md:w-auto flex items-center gap-2 bg-blue-50/40 backdrop-blur-md hover:bg-blue-100/50 border-blue-200/60 text-blue-700 shadow-lg transition-all duration-300"
                  style={{
                    boxShadow: '0 4px 16px rgba(59,130,246,0.1), inset 0 1px 0 rgba(255,255,255,0.3)',
                  }}
                >
                  <Smartphone className="h-4 w-4" />
                  Install me as an app!
                </Button>
              ) : null}
            </div>
          </div>

          {Object.keys(groupedEvents).length === 0 ? (
            <div className="text-center py-12">
              <Music className="h-16 w-16 mx-auto mb-4 text-slate-400" />
              <h3 className="text-lg font-medium text-slate-900 mb-2">
                No events found
              </h3>
              <p className="text-slate-600 mb-6">
                You are not part of any organisations with events.
              </p>
            </div>
          ) : (
            <Accordion type="single" collapsible className="w-full space-y-4" defaultValue={Object.keys(groupedEvents)[0]}>
              {Object.entries(groupedEvents).map(([orgName, { org, upcomingEvents, pastEvents }]) => (
                <AccordionItem value={orgName} key={orgName}>
                  <AccordionTrigger className="text-xl font-bold text-slate-800 bg-white/30 backdrop-blur-md px-4 rounded-xl hover:bg-white/40 border border-white/20 shadow-lg transition-all duration-300 flex justify-between"
                    style={{
                      boxShadow: '0 4px 16px rgba(0,0,0,0.05), inset 0 1px 0 rgba(255,255,255,0.3)',
                    }}>
                    <span className="flex items-center gap-2">
                      {org?.logoUrl ? (
                        <Squircle 
                          src={org.logoUrl} 
                          alt={`${orgName} Logo`} 
                          size={20}
                          className="flex-shrink-0"
                        />
                      ) : (
                        <Building className="h-5 w-5 text-slate-600 drop-shadow-sm flex-shrink-0" />
                      )}
                      <span className="drop-shadow-sm">{orgName} ({upcomingEvents.length + pastEvents.length})</span>
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="px-4 pt-4">
                    <div className="mb-4 flex flex-col md:flex-row gap-2">
                      <div className="flex flex-row gap-2 w-full md:w-auto">
                        {userIsOwnerOfOrg(org?.id) && (
                            <Button
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                if (org?.id) {
                                  handleCreateEvent(org.id);
                                }
                              }}
                              className="w-full md:w-auto relative z-10 bg-primary hover:bg-blue-600 text-white"
                            >
                              <Plus className="mr-2 h-4 w-4" />
                              New Event
                            </Button>
                        )}
                        {userIsOwnerOfOrg(org?.id) && (
                            <Button
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                if (org?.id) {
                                  setLocation(`/analytics/${org.id}`);
                                }
                              }}
                              variant="outline"
                              className="w-full md:w-auto relative z-10 flex items-center gap-2"
                            >
                              <BarChart3 className="h-4 w-4" />
                              Analytics
                            </Button>
                        )}
                      </div>
                      <div className="relative flex-grow">
                        <Input
                          type="text"
                          placeholder="Search events..."
                          className="w-full"
                          value={searchTerms[orgName] || ''}
                          onChange={(e) => handleSearchChange(orgName, e.target.value)}
                        />
                      </div>
                    </div>
                    {(upcomingEvents.length > 0 || pastEvents.length > 0) ? (
                      <div className="space-y-6">
                        {/* Upcoming Events */}
                        {upcomingEvents.length > 0 && (
                          <div>
                            <h4 className="text-lg font-semibold text-slate-800 mb-4 drop-shadow-sm">
                              Upcoming Events ({upcomingEvents.length})
                            </h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                              {upcomingEvents.map(event => <EventCard key={event.id} event={event} onSelect={handleSelectEvent} />)}
                            </div>
                          </div>
                        )}
                        
                        {/* Past Events */}
                        {pastEvents.length > 0 && (
                          <div className="w-full">
                            <div className="mb-4">
                              <button
                                onClick={() => togglePastEvents(orgName)}
                                className="text-lg font-semibold text-slate-700 px-4 rounded-xl hover:bg-white/20 transition-all duration-300 flex items-center justify-between w-full"
                              >
                                <span className="flex items-center gap-2">
                                  <Calendar className="h-4 w-4 text-slate-600 drop-shadow-sm" />
                                  <span className="drop-shadow-sm">Past Events ({pastEvents.length})</span>
                                </span>
                                <ChevronDown 
                                  className={`h-4 w-4 shrink-0 transition-transform duration-200 ${expandedPastEvents[orgName] ? 'rotate-180' : ''}`}
                                />
                              </button>
                            </div>
                            {expandedPastEvents[orgName] && (
                              <div className="pt-4">
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                  {pastEvents.map(event => <EventCard key={event.id} event={event} onSelect={handleSelectEvent} />)}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-slate-500">
                        <p>No events have been created for this organisation yet.</p>
                        {userIsOwnerOfOrg(org?.id) ? (
                          <p className="text-sm mt-1">Click the button above to create one.</p>
                        ) : (
                          <p className="text-sm mt-1">Contact an owner to create one.</p>
                        )}
                      </div>
                    )}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          )}

        </main>

        {/* Event Management Modals now handled by EventActionsDropdown component */}

        {typeof showCreateModal === 'number' && showCreateModal > 0 && (
          <CreateEventModal
            isOpen={true}
            onOpenChange={() => setShowCreateModal(false)}
            organisationId={showCreateModal}
          />
        )}

        <HelpModal isOpen={showHelpModal} onOpenChange={setShowHelpModal} />
        <PWAInstallModal isOpen={showPWAInstallModal} onOpenChange={setShowPWAInstallModal} />
    </PageBackground>
  );
}
